import { alpha, createTheme } from "@mui/material/styles";
import {
  brandRgba,
  DEFAULT_BRAND,
  getBrandMode,
} from "../config/brand-colors";
import { DARK_SURFACE, LIGHT_SURFACE } from "./surfaceTokens";
import { getAppBrand } from "../config/resolve-app-brand";

const sharedTypography = {
  fontFamily: "'Inter', 'Segoe UI', Roboto, sans-serif",
  h1: { fontWeight: 700, fontSize: "56px", lineHeight: 1.1, letterSpacing: "-1px" },
  h2: { fontWeight: 700, fontSize: "32px", lineHeight: 1.2, letterSpacing: "-0.5px" },
  h3: { fontWeight: 600, fontSize: "20px" },
  body1: { fontSize: "15px" },
  body2: { fontSize: "14px" },
};

const sharedShape = { borderRadius: 12 };
const sharedBreakpoints = { values: { xs: 0, sm: 600, md: 768, lg: 1024, xl: 1280 } };

export const createRegisterPageDarkTheme = (brand = DEFAULT_BRAND) => {
  const mode = getBrandMode(brand, true);
  const primary = mode.main;
  const primaryHover = mode.hover;
  const contrastText = mode.contrastText ?? "#ffffff";

  return createTheme({
    palette: {
      mode: "dark",
      primary: {
        main: primary,
        light: brandRgba(primary, 0.12),
        dark: primaryHover,
        contrastText,
      },
      secondary: {
        main: mode.chartMuted,
        light: brandRgba(mode.chartMuted, 0.15),
        dark: brandRgba(mode.chartMuted, 0.3),
      },
      text: { primary: DARK_SURFACE.textPrimary, secondary: DARK_SURFACE.textSecondary },
      background: { default: DARK_SURFACE.bgDefault, paper: DARK_SURFACE.bgPaper },
      divider: DARK_SURFACE.divider,
      error: { main: "#f44336" },
    },
    typography: sharedTypography,
    shape: sharedShape,
    breakpoints: sharedBreakpoints,
    components: {
      MuiPaper: { styleOverrides: { root: { backgroundImage: "none" } } },
      MuiTextField: {
        defaultProps: {
          size: "medium",
          InputLabelProps: { shrink: true },
        },
      },
      MuiSelect: { defaultProps: { size: "medium" } },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            backgroundColor: DARK_SURFACE.inputBg,
            borderRadius: 12,
            fontSize: 15,
            transition: "border-color 0.3s ease, box-shadow 0.3s ease, background 0.3s ease",
            "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: primary },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: primary },
            "&.Mui-focused": { boxShadow: `0 0 0 4px ${brandRgba(primary, 0.1)}` },
            "&.Mui-disabled": { backgroundColor: "rgba(255,255,255,0.02)" },
          },
          input: {
            padding: "14px 20px",
            fontSize: 15,
            "&::placeholder": { color: DARK_SURFACE.textSecondary, opacity: 1 },
          },
          notchedOutline: { borderColor: DARK_SURFACE.divider },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            color: DARK_SURFACE.textSecondary,
            fontSize: 15,
            "&.Mui-focused": { color: primary },
          },
          outlined: {
            transform: "translate(14px, -9px) scale(0.85)",
            "&.MuiInputLabel-shrink": { transform: "translate(14px, -9px) scale(0.85)" },
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: "none",
            fontWeight: 600,
            borderRadius: 12,
            fontSize: 16,
            padding: "14px 24px",
            fontFamily: "'Inter', 'Segoe UI', Roboto, sans-serif",
          },
          sizeSmall: { fontSize: 14, padding: "8px 16px", borderRadius: 8 },
          sizeLarge: { fontSize: 16, padding: "16px 24px" },
          contained: {
            background: primary,
            color: contrastText,
            boxShadow: `0 4px 20px ${brandRgba(primary, 0.3)}`,
            "&:hover": {
              background: primaryHover,
              boxShadow: `0 8px 30px ${brandRgba(primary, 0.5)}`,
              transform: "translateY(-2px)",
            },
            "&.Mui-disabled": {
              background: brandRgba(primary, 0.35),
              color: alpha(contrastText, 0.7),
              boxShadow: "none",
              transform: "none",
            },
            transition: "all 0.3s ease",
          },
          outlined: {
            borderColor: primary,
            color: primary,
            "&:hover": { borderColor: primary, background: brandRgba(primary, 0.1) },
          },
        },
      },
      MuiCheckbox: {
        styleOverrides: {
          root: {
            color: "rgba(255,255,255,0.25)",
            "&.Mui-checked": { color: primary },
          },
        },
      },
      MuiLink: {
        styleOverrides: {
          root: { color: primary, "&:hover": { color: primaryHover } },
        },
      },
    },
  });
};

export const createRegisterPageLightTheme = (brand = DEFAULT_BRAND) => {
  const mode = getBrandMode(brand, false);
  const primary = mode.main;
  const primaryHover = mode.hover;

  return createTheme({
    palette: {
      mode: "light",
      primary: {
        main: primary,
        light: brandRgba(primary, 0.08),
        dark: primaryHover,
      },
      secondary: {
        main: mode.chartMuted,
        light: alpha(mode.chartLight, 0.8),
        dark: alpha(mode.chartMuted, 0.3),
      },
      text: { primary: LIGHT_SURFACE.textPrimary, secondary: LIGHT_SURFACE.textSecondary },
      background: { default: LIGHT_SURFACE.bgDefault, paper: LIGHT_SURFACE.bgPaper },
      divider: LIGHT_SURFACE.divider,
      error: { main: "#d32f2f" },
    },
    typography: sharedTypography,
    shape: sharedShape,
    breakpoints: sharedBreakpoints,
    components: {
      MuiPaper: { styleOverrides: { root: { backgroundImage: "none" } } },
      MuiTextField: {
        defaultProps: {
          size: "medium",
          InputLabelProps: { shrink: true },
        },
      },
      MuiSelect: { defaultProps: { size: "medium" } },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            backgroundColor: LIGHT_SURFACE.inputBg,
            borderRadius: 12,
            fontSize: 15,
            transition: "border-color 0.3s ease, box-shadow 0.3s ease",
            "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: primary },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: primary },
            "&.Mui-focused": { boxShadow: `0 0 0 4px ${brandRgba(primary, 0.1)}` },
          },
          input: {
            padding: "14px 20px",
            fontSize: 15,
            "&::placeholder": { color: LIGHT_SURFACE.textSecondary, opacity: 1 },
          },
          notchedOutline: { borderColor: LIGHT_SURFACE.divider },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: { color: LIGHT_SURFACE.textSecondary, fontSize: 15, "&.Mui-focused": { color: primary } },
          outlined: {
            transform: "translate(14px, -9px) scale(0.85)",
            "&.MuiInputLabel-shrink": { transform: "translate(14px, -9px) scale(0.85)" },
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: "none",
            fontWeight: 600,
            borderRadius: 12,
            fontSize: 16,
            padding: "14px 24px",
            fontFamily: "'Inter', 'Segoe UI', Roboto, sans-serif",
          },
          sizeSmall: { fontSize: 14, padding: "8px 16px", borderRadius: 8 },
          sizeLarge: { fontSize: 16, padding: "16px 24px" },
          contained: {
            background: primary,
            color: "#fff",
            boxShadow: `0 4px 20px ${brandRgba(primary, 0.25)}`,
            "&:hover": {
              background: primaryHover,
              boxShadow: `0 8px 30px ${brandRgba(primary, 0.35)}`,
              transform: "translateY(-2px)",
            },
            "&.Mui-disabled": {
              background: brandRgba(primary, 0.35),
              color: "rgba(255,255,255,0.85)",
              boxShadow: "none",
              transform: "none",
            },
            transition: "all 0.3s ease",
          },
          outlined: {
            borderColor: primary,
            color: primary,
            "&:hover": { borderColor: primary, background: brandRgba(primary, 0.06) },
          },
        },
      },
      MuiCheckbox: {
        styleOverrides: {
          root: { "&.Mui-checked": { color: primary } },
        },
      },
      MuiLink: {
        styleOverrides: {
          root: { color: primary, "&:hover": { color: primaryHover } },
        },
      },
    },
  });
};

/** @deprecated Use createRegisterPageDarkTheme(getAppBrand()) */
export const registerPageDarkTheme = createRegisterPageDarkTheme(getAppBrand());
/** @deprecated Use createRegisterPageLightTheme(getAppBrand()) */
export const registerPageLightTheme = createRegisterPageLightTheme(getAppBrand());
